<template>
  <div class="detail-stats">
    <!-- 动态标题 -->
    <div v-if="title" class="stats-header">
      <div class="stats-title">{{ title }}</div>
    </div>
    
    <div class="stats-content">
      <!-- 基本信息统计 -->
      <div v-if="basicStats && basicStats.length > 0" class="basic-stats">
        <div
          v-for="(stat, index) in basicStats"
          :key="index"
          class="basic-stat-card"
        >
          <div class="stat-label">{{ stat.label }}</div>
          <div class="stat-value" :style="{ color: getStatColor(stat) }">
            {{ formatStatValue(stat) }}<span v-if="stat.unit && !shouldHideUnitDefault(stat)" class="stat-unit">{{ stat.unit }}</span>
          </div>
        </div>
      </div>
      
      <!-- 统计图 -->
      <div v-if="chartGroups && chartGroups.length > 0" class="chart-stats">
        <div
          v-for="(group, groupIndex) in chartGroups"
          :key="groupIndex"
          class="chart-stat-item"
        >
          <div class="chart-title">{{ group.title }}</div>
          <div :ref="`chartGroupRef${groupIndex}`" class="chart-container"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts'

export default {
  name: 'DetailStats',
  props: {
    // 标题
    title: {
      type: String,
      default: ''
    },
    // 基本信息统计卡片数据
    basicStats: {
      type: Array,
      default: () => []
      // 格式: [{ label: '标签', value: '值', unit: '单位', field: '字段名', colorType: 'green|blue|yellow|red' }]
    },
    // 图表分组数据
    chartGroups: {
      type: Array,
      default: () => []
      // 格式: [{ title: '图表标题', fields: ['field1', 'field2'], series: [...] }]
    },
    // 趋势数据（用于图表）
    trendData: {
      type: Object,
      default: null
      // 格式: { hours: ['00:00', ...], series: [{ name: '名称', field: '字段', data: [...], unit: '单位' }] }
    },
    // 自定义格式化函数
    formatValue: {
      type: Function,
      default: null
    },
    // 自定义颜色获取函数
    getColor: {
      type: Function,
      default: null
    },
    // 自定义单位隐藏判断函数
    shouldHideUnit: {
      type: Function,
      default: null
    }
  },
  data() {
    return {
      groupedCharts: [], // 存储分组图表的实例
      resizeHandler: null // 窗口resize事件处理器
    }
  },
  watch: {
    // 监听数据变化，更新图表
    trendData: {
      handler(newVal) {
        if (newVal && newVal.series) {
          this.$nextTick(() => {
            this.updateGroupedCharts()
          })
        }
      },
      immediate: false
    },
    chartGroups: {
      handler(newVal) {
        if (newVal && newVal.length > 0) {
          this.$nextTick(() => {
            this.initGroupedCharts()
          })
        }
      },
      immediate: false
    }
  },
  mounted() {
    // 延迟初始化，确保 DOM 完全渲染
    this.$nextTick(() => {
      this.initGroupedCharts()
    })
    this.resizeHandler = () => {
      this.groupedCharts.forEach(chart => {
        if (chart) chart.resize()
      })
    }
    window.addEventListener('resize', this.resizeHandler)
  },
  methods: {
    // 判断是否应该隐藏单位
    shouldHideUnitDefault(stat) {
      // 默认实现：可以根据 label 判断，比如风向不需要单位
      if (this.shouldHideUnit) {
        return this.shouldHideUnit(stat.label || stat)
      }
      return false
    },
    // 格式化指标显示值
    formatStatValue(stat) {
      if (this.formatValue) {
        return this.formatValue(stat.label, stat.value, stat)
      }
      return stat.value
    },
    // 获取指标值的颜色
    getStatColor(stat) {
      if (this.getColor) {
        return this.getColor(stat.label, stat.value, stat)
      }
      // 如果有 colorType，使用默认颜色映射
      if (stat.colorType) {
        const colorMap = {
          'green': '#4caf50',
          'blue': '#00d4ff',
          'yellow': '#ffc107',
          'red': '#ff5a5a'
        }
        return colorMap[stat.colorType] || '#00d4ff'
      }
      // 默认颜色
      return '#00d4ff'
    },
    // 更新分组图表
    updateGroupedCharts() {
      if (!this.trendData || !this.trendData.series || !this.chartGroups || this.chartGroups.length === 0) {
        return
      }

      const hours = this.trendData.hours || []
      const allSeries = this.trendData.series || []

      if (!hours.length || !allSeries.length) {
        return
      }

      this.chartGroups.forEach((group, groupIndex) => {
        const chart = this.groupedCharts[groupIndex]
        if (!chart) {
          return
        }

        try {

        // 筛选出该组对应的series
        const groupSeries = allSeries.filter(s => group.fields && group.fields.includes(s.field))

        if (groupSeries.length === 0) {
          return
        }

        // 确保series有name字段，并确保数据长度与hours一致
        const seriesWithName = groupSeries.map(s => {
          if (!s.name && s.field) {
            // 尝试从 basicStats 中查找对应的 label
            const stat = this.basicStats.find(bs => bs.field === s.field)
            if (stat) {
              s.name = stat.label
              s.unit = s.unit || stat.unit
            } else {
              s.name = s.field
            }
          }
          // 确保数据长度与hours数组长度一致，并转换为数字
          if (s.data && Array.isArray(s.data)) {
            const expectedLength = hours.length
            let processedData = s.data.map(v => {
              if (v == null || v === '' || isNaN(v)) {
                return 0
              }
              const numValue = parseFloat(v)
              return isNaN(numValue) ? 0 : numValue
            })

            if (processedData.length < expectedLength) {
              processedData = [...processedData, ...new Array(expectedLength - processedData.length).fill(0)]
            } else if (processedData.length > expectedLength) {
              processedData = processedData.slice(0, expectedLength)
            }
            s.data = processedData
          } else {
            s.data = new Array(hours.length).fill(0)
          }
          return s
        })

        // 定义颜色映射
        const colorMap = {
          '风速': '#00d4ff',
          '风向': '#4caf50',
          '大气温度': '#ff9800',
          '大气湿度': '#9c27b0',
          '大气压力': '#ff1744',
          'PM2.5浓度': '#ff1744',
          'PM10浓度': '#00e676',
          '光照度': '#ffc107',
          '环境噪声': '#ff6f00'
        }

        // 获取所有数据值，用于计算Y轴范围
        const allValues = []
        seriesWithName.forEach(s => {
          if (s.data && Array.isArray(s.data)) {
            allValues.push(...s.data.filter(v => v != null && !isNaN(v) && v !== ''))
          }
        })

        // 计算合适的Y轴范围和间隔
        const calculateYAxisRange = (values, minConstraint = null) => {
          if (values.length === 0) {
            return { min: 0, max: 100, interval: 20 }
          }

          const dataMin = Math.min(...values)
          const dataMax = Math.max(...values)

          if (dataMin === dataMax) {
            const center = dataMin
            const range = Math.max(center * 0.1, 10)
            return {
              min: Math.max(0, center - range / 2),
              max: center + range / 2,
              interval: range / 5
            }
          }

          const dataRange = dataMax - dataMin
          const margin = dataRange * 0.05
          let yMin = dataMin - margin
          let yMax = dataMax + margin

          if (minConstraint !== null && yMin < minConstraint) {
            yMin = minConstraint
          }

          if (yMin < 0 && dataMin >= 0) {
            yMin = 0
          }

          const range = yMax - yMin
          let interval

          if (range <= 10) {
            interval = 1
          } else if (range <= 50) {
            interval = 5
          } else if (range <= 100) {
            interval = 10
          } else if (range <= 500) {
            interval = 50
          } else if (range <= 1000) {
            interval = 100
          } else {
            interval = Math.ceil(range / 10)
          }

          yMin = Math.floor(yMin / interval) * interval
          yMax = Math.ceil(yMax / interval) * interval

          return { min: yMin, max: yMax, interval }
        }

        let yMin, yMax, yAxisFormatter, yAxisInterval
        if (group.title === '大气压力') {
          const pressureValues = allValues.filter(v => v > 0)
          if (pressureValues.length > 0) {
            const range = calculateYAxisRange(pressureValues, 100)
            yMin = range.min
            yMax = range.max
            yAxisInterval = range.interval
            yAxisFormatter = (value) => value.toFixed(1)
          } else {
            yMin = 100
            yMax = 1100
            yAxisInterval = 200
            yAxisFormatter = (value) => Math.round(value).toString()
          }
        } else if (group.title === '光照度') {
          const illuminationValues = allValues.filter(v => v >= 0)
          if (illuminationValues.length > 0) {
            const range = calculateYAxisRange(illuminationValues, 0)
            yMin = range.min
            yMax = range.max
            yAxisInterval = range.interval
          } else {
            yMin = 0
            yMax = 2000
            yAxisInterval = 500
          }
          yAxisFormatter = (value) => Math.round(value).toString()
        } else {
          const validValues = allValues.filter(v => v >= 0)
          if (validValues.length > 0) {
            const range = calculateYAxisRange(validValues, 0)
            yMin = range.min
            yMax = range.max
            yAxisInterval = range.interval
          } else {
            yMin = 0
            yMax = 100
            yAxisInterval = 20
          }
          yAxisFormatter = (value) => Math.round(value).toString()
        }

        const yAxisConfig = {
          type: 'value',
          min: yMin,
          max: yMax,
          interval: yAxisInterval,
          axisLine: {
            show: true,
            lineStyle: {
              color: 'rgba(160, 179, 200, 0.3)',
              width: 1
            }
          },
          axisLabel: {
            color: '#a0b3c8',
            fontSize: 10,
            formatter: yAxisFormatter,
            margin: 6
          },
          axisTick: {
            show: true,
            lineStyle: { color: 'rgba(160, 179, 200, 0.2)' }
          },
          splitLine: {
            show: true,
            lineStyle: {
              color: 'rgba(255,255,255,0.05)',
              type: 'dashed',
              width: 1
            }
          }
        }

        chart.setOption({
          backgroundColor: 'transparent',
          tooltip: {
            trigger: 'axis',
            backgroundColor: 'rgba(15, 30, 50, 0.95)',
            borderColor: 'rgba(0, 212, 255, 0.5)',
            borderWidth: 1,
            textStyle: { color: '#fff', fontSize: 11 },
            axisPointer: {
              type: 'line',
              lineStyle: {
                color: 'rgba(0, 212, 255, 0.5)',
                width: 1,
                type: 'dashed'
              },
              label: {
                backgroundColor: 'rgba(0, 212, 255, 0.8)',
                color: '#fff'
              }
            },
            formatter: (params) => {
              let result = `<div style="margin-bottom: 4px; font-weight: bold;">${params[0].axisValue}</div>`
              params.forEach(param => {
                const series = seriesWithName.find(s => s.name === param.seriesName)
                const unit = series && series.unit ? series.unit : ''
                result += `<div style="margin: 2px 0;">
                  <span style="display: inline-block; width: 10px; height: 10px; background: ${param.color}; border-radius: 50%; margin-right: 6px;"></span>
                  ${param.seriesName}: <span style="font-weight: bold;">${param.value}${unit}</span>
                </div>`
              })
              return result
            }
          },
          legend: {
            data: seriesWithName.map(s => s.name),
            bottom: 0,
            left: 'center',
            textStyle: {
              color: '#fff',
              fontSize: 11
            },
            itemGap: 12,
            itemWidth: 12,
            itemHeight: 12
          },
          grid: {
            left: 45,
            right: 20,
            top: 25,
            bottom: 60,
            containLabel: false
          },
          xAxis: {
            type: 'category',
            data: hours,
            boundaryGap: false,
            axisLine: {
              show: true,
              lineStyle: {
                color: 'rgba(160, 179, 200, 0.3)',
                width: 1
              }
            },
            axisLabel: {
              color: '#a0b3c8',
              fontSize: 10,
              interval: 0,
              rotate: 45,
              formatter: (value) => {
                return String(value || '')
              },
              margin: 12
            },
            axisTick: {
              show: true,
              alignWithLabel: true,
              lineStyle: { color: 'rgba(160, 179, 200, 0.2)' },
              length: 4
            },
            splitLine: {
              show: false
            }
          },
          yAxis: yAxisConfig,
          series: seriesWithName.map((s, index) => {
            const color = colorMap[s.name] || ['#00d4ff', '#4caf50', '#ffc107', '#ff9800'][index % 4]
            return {
              name: s.name,
              type: 'line',
              data: s.data || [],
              smooth: true,
              lineStyle: {
                color,
                width: 2.5,
                shadowBlur: 4,
                shadowColor: color + '40'
              },
              itemStyle: {
                color,
                borderColor: '#fff',
                borderWidth: 1.5,
                shadowBlur: 3,
                shadowColor: color + '60'
              },
              symbol: 'circle',
              symbolSize: 5,
              yAxisIndex: 0,
              emphasis: {
                focus: 'series',
                lineStyle: {
                  width: 3
                },
                itemStyle: {
                  symbolSize: 7
                }
              },
              areaStyle: {
                color: {
                  type: 'linear',
                  x: 0, y: 0, x2: 0, y2: 1,
                  colorStops: [
                    { offset: 0, color: color + '50' },
                    { offset: 1, color: color + '05' }
                  ]
                }
              }
            }
          })
        })

        chart.resize()
        } catch (error) {
          console.warn(`更新图表 ${group.title} 失败:`, error)
        }
      })
    },
    // 初始化分组图表
    initGroupedCharts() {
      if (!this.chartGroups || this.chartGroups.length === 0) {
        return
      }
      this.$nextTick(() => {
        try {
          this.chartGroups.forEach((group, index) => {
            const refName = `chartGroupRef${index}`
            const chartElement = this.$refs[refName]
            if (chartElement && chartElement[0] && chartElement[0].offsetWidth > 0) {
              // 确保元素已渲染且有宽度
              if (!this.groupedCharts[index]) {
                this.groupedCharts[index] = echarts.init(chartElement[0])
              }
            }
          })
          // 只有在有图表实例时才更新
          if (this.groupedCharts.some(chart => chart)) {
            this.updateGroupedCharts()
          }
        } catch (error) {
          console.warn('初始化图表失败:', error)
        }
      })
    }
  },
  beforeDestroy() {
    // 销毁分组图表
    this.groupedCharts.forEach(chart => {
      if (chart) {
        chart.dispose()
      }
    })
    this.groupedCharts = []
    // 移除窗口resize事件监听
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler)
    }
  }
}
</script>

<style lang="scss" scoped>
.detail-stats {
  height: 100%;
  width: 100%;
  padding: 20px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 15px;
  overflow: hidden;
  min-height: 0;
}

.stats-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 15px;
  user-select: none;
  transition: background-color 0.2s;

  &:hover {
    background: rgba(0, 212, 255, 0.1);
  }

  .stats-title {
    color: #00d4ff;
    font-size: 15px;
    font-weight: bold;
    display: flex;
    align-items: center;
    gap: 8px;
  }
}

.stats-content {
  flex: 1;
  min-height: 0;
  padding: 15px;
  text-align: left;
  overflow-y: auto;
  overflow-x: hidden;

  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

.basic-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 20px;
  justify-content: flex-start;
  align-items: flex-start;
}

.basic-stat-card {
  flex: 0 0 auto;
  min-width: 150px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  padding: 12px 18px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;

  .stat-label {
    font-size: 14px;
    color: #a0b3c8;
    margin-bottom: 8px;
  }

  .stat-value {
    font-size: 24px;
    font-weight: bold;
    color: #a0b3c8;
    white-space: nowrap;
    line-height: 1.2;

    .stat-unit {
      font-size: 14px;
      margin-left: 4px;
      font-weight: normal;
    }
  }
}

.chart-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  justify-content: flex-start;
  align-items: flex-start;
}

.chart-stat-item {
  flex: 0 0 auto;
  min-width: 500px;
  background: rgba(0, 212, 255, 0.05);
  border: 1px solid rgba(0, 212, 255, 0.2);
  border-radius: 8px;
  padding: 12px;
  display: flex;
  flex-direction: column;

  .chart-title {
    color: #00d4ff;
    font-size: 13px;
    font-weight: bold;
    margin-bottom: 8px;
    text-align: left;
  }

  .chart-container {
    width: 100%;
    height: 220px;
    min-height: 220px;
  }
}
</style>

